import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { getErpAuthState, isErpApiSuccess } from "../erpClient";
import { getUserData } from "../pages/const";
import Pagination from "./Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { hrmsApi } from "../utils/hrmsApi";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function formatDateDdMmYyyy(iso) {
  const s = String(iso || "").slice(0, 10);
  const parts = s.split("-");
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
}

function resolveEssUser() {
  const erp = getErpAuthState().user || {};
  const legacy = getUserData() || {};
  const uaId = Number(
    erp.id ?? erp.ua_id ?? legacy.id ?? legacy.ua_id ?? legacy.user_id,
  );
  const branchId = Number(
    erp.branch_id ?? erp.branchId ?? legacy.branch_id ?? legacy.branchId,
  );
  return {
    uaId: Number.isFinite(uaId) && uaId > 0 ? uaId : null,
    branchId: Number.isFinite(branchId) && branchId > 0 ? branchId : null,
  };
}

function todayIsoDate() {
  return new Date().toISOString().split("T")[0];
}

function monthKey(year, monthIndex0) {
  return `${year}-${String(monthIndex0 + 1).padStart(2, "0")}`;
}

function toHm(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  const hmOnly = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hmOnly) return `${hmOnly[1].padStart(2, "0")}:${hmOnly[2]}`;
  const tailHm = s.match(/(\d{1,2}):(\d{2})(?::\d{2})?\s*$/);
  if (tailHm) return `${tailHm[1].padStart(2, "0")}:${tailHm[2]}`;
  const tailHourOnly = s.match(/(\d{1,2}):\s*$/);
  if (tailHourOnly) return `${tailHourOnly[1].padStart(2, "0")}:00`;
  const parsed = Date.parse(s.includes("T") ? s : s.replace(" ", "T"));
  if (Number.isFinite(parsed)) {
    const d = new Date(parsed);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  const m = s.match(/(\d{1,2}):(\d{2})/);
  return m ? `${m[1].padStart(2, "0")}:${m[2]}` : "";
}

function formatDisplayTime(hm) {
  const t = String(hm || "").trim();
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return t;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d.toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatMinutes(mins) {
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return "";
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h <= 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function daysInMonth(year, monthIndex0) {
  return new Date(year, monthIndex0 + 1, 0).getDate();
}

/** Days in the viewed month that count toward absent (excludes future dates). */
function countEligibleDaysInMonth(year, monthIndex0, todayIso) {
  const total = daysInMonth(year, monthIndex0);
  const cur = new Date();
  const isCurrentView =
    year === cur.getFullYear() && monthIndex0 === cur.getMonth();
  if (!isCurrentView) return total;
  const todayDay = Number(String(todayIso).slice(8, 10));
  return Number.isFinite(todayDay) && todayDay > 0
    ? Math.min(todayDay, total)
    : total;
}

function parseDurationToMinutes(duration) {
  const s = String(duration || "").trim();
  if (!s || s === "—" || s === "-") return 0;
  const hm = s.match(/^(\d+)h\s*(\d+)m$/i);
  if (hm) {
    const h = Number(hm[1]);
    const m = Number(hm[2]);
    if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
  }
  const hOnly = s.match(/^(\d+)h$/i);
  if (hOnly) {
    const h = Number(hOnly[1]);
    if (Number.isFinite(h) && h > 0) return h * 60;
  }
  const mOnly = s.match(/^(\d+)m$/i);
  if (mOnly) {
    const m = Number(mOnly[1]);
    if (Number.isFinite(m) && m > 0) return m;
  }
  return 0;
}

/** Minutes for one day — same sources as the Duration column. */
function resolveWorkedMins(row) {
  const n = Number(row?.workedMins);
  if (Number.isFinite(n) && n > 0) return n;
  const fromDuration = parseDurationToMinutes(row?.duration);
  if (fromDuration > 0) return fromDuration;
  return computeWorkedMinutesFromHm(row?.punch_in, row?.punch_out);
}

function computeMonthSummary(viewYear, viewMonth, dayRows, todayIso) {
  const eligibleDays = countEligibleDaysInMonth(viewYear, viewMonth, todayIso);

  let present = 0;
  let totalMins = 0;

  for (const row of dayRows) {
    if (!row.iso) continue;
    // Total hours: sum every day in this month's log (no "month must end" / today cutoff).
    totalMins += resolveWorkedMins(row);
    // Present / absent: only calendar days that have already occurred.
    if (row.iso > todayIso) continue;
    if (String(row.punch_in || "").trim()) present += 1;
  }

  return {
    present,
    absent: Math.max(0, eligibleDays - present),
    totalHours: totalMins > 0 ? formatMinutes(totalMins) : "0h",
  };
}

function computeWorkedMinutesFromHm(punchIn, punchOut) {
  const a = String(punchIn || "").trim();
  const b = String(punchOut || "").trim();
  if (!a || !b) return 0;
  const [ah, am] = a.split(":").map(Number);
  const [bh, bm] = b.split(":").map(Number);
  if (
    !Number.isFinite(ah) ||
    !Number.isFinite(am) ||
    !Number.isFinite(bh) ||
    !Number.isFinite(bm)
  ) {
    return 0;
  }
  let mins = bh * 60 + bm - (ah * 60 + am);
  if (mins < 0) mins += 24 * 60;
  return mins > 0 ? mins : 0;
}

function pickMyEmployeeRecord(data, uaId) {
  const employees = data?.employees;
  if (!Array.isArray(employees) || employees.length === 0) return null;
  const id = Number(uaId);
  if (!Number.isFinite(id) || id <= 0) return null;
  return (
    employees.find(
      (e) => Number(e.user_admin_id ?? e.employee_id ?? e.ua_id) === id,
    ) ?? null
  );
}

function normalizeDayRow(row) {
  const iso = String(
    row?.work_date ?? row?.date ?? row?.attendance_date ?? "",
  ).slice(0, 10);
  const punchIn = toHm(
    row?.first_in ??
      row?.punch_in ??
      row?.punch_in_time ??
      row?.in_time ??
      row?.check_in,
  );
  const punchOut = toHm(
    row?.last_out ??
      row?.punch_out ??
      row?.punch_out_time ??
      row?.out_time ??
      row?.check_out,
  );
  const durationMins = Number(
    row?.duration_minutes ??
      row?.worked_minutes ??
      row?.work_minutes ??
      row?.total_minutes,
  );
  const fromPunchMins = computeWorkedMinutesFromHm(punchIn, punchOut);
  const workedMins =
    Number.isFinite(durationMins) && durationMins > 0
      ? durationMins
      : fromPunchMins;

  let duration = "";
  if (workedMins > 0) {
    duration = formatMinutes(workedMins);
  } else {
    const wh = Number(row?.worked_hours ?? row?.hours);
    if (Number.isFinite(wh) && wh > 0) {
      duration = `${wh}h`;
    } else {
      const raw = String(row?.duration ?? row?.worked_duration ?? "").trim();
      if (raw && raw !== "0" && raw !== "0h") duration = raw;
    }
  }

  return {
    iso,
    punch_in: punchIn,
    punch_out: punchOut,
    duration,
    workedMins,
  };
}

function AttendanceStat({ label, value }) {
  return (
    <div className="h-100 py-3 px-3 bg-transparent">
      <div className="text-dark fs-4 mb-1">{label}</div>
      <div className="text-dark mb-0 tabular-nums">{value}</div>
    </div>
  );
}

function extractDayRows(data, uaId) {
  const emp = pickMyEmployeeRecord(data, uaId);
  const list = emp
    ? emp.days
    : (data?.days ??
      data?.daily_records ??
      data?.records ??
      data?.attendance_days ??
      data?.day_list ??
      (Array.isArray(data) ? data : []));
  return Array.isArray(list) ? list.map((r) => normalizeDayRow(r)) : [];
}

export function EssMyAttendanceTab() {
  const { cardClass, tableClass } = useButtonTableStyle();
  const [authTick, setAuthTick] = useState(0);
  const now = new Date();
  const [viewYear, setViewYear] = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [dayRows, setDayRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const baseUser = useMemo(() => resolveEssUser(), [authTick]);
  const today = todayIsoDate();

  const user = useMemo(() => baseUser, [baseUser]);

  useEffect(() => {
    const bump = () => setAuthTick((n) => n + 1);
    window.addEventListener("erpAuthUpdated", bump);
    window.addEventListener("permissionsUpdated", bump);
    return () => {
      window.removeEventListener("erpAuthUpdated", bump);
      window.removeEventListener("permissionsUpdated", bump);
    };
  }, []);

  const monthLabel = `${MONTH_NAMES[viewMonth]} ${viewYear}`;

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setCurrentPage(1);
  };

  const monthSummary = useMemo(
    () => computeMonthSummary(viewYear, viewMonth, dayRows, today),
    [viewYear, viewMonth, dayRows, today],
  );

  const displayedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return dayRows.slice(start, start + itemsPerPage);
  }, [dayRows, currentPage, itemsPerPage]);

  const handlePageChange = (newPage) => {
    const totalPages = Math.max(1, Math.ceil(dayRows.length / itemsPerPage));
    if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const loadMonth = useCallback(async () => {
    if (!user.branchId) {
      setDayRows([]);
      return;
    }

    setLoading(true);
    try {
      const res = await hrmsApi("attendance_month_summary", {
        branch_id: Number(user.branchId),
        month: monthKey(viewYear, viewMonth),
      });

      if (!isErpApiSuccess(res)) {
        setDayRows([]);
        hrmsErrorToast(
          new Error(res?.message || "Could not load attendance."),
          "Could not load attendance.",
        );
        return;
      }

      const data = res?.data?.data ?? res?.data ?? {};
      const rows = extractDayRows(data, user.uaId)
        .filter((r) => r.iso)
        .sort((a, b) => b.iso.localeCompare(a.iso))
        .map((r) => ({
          ...r,
          isToday: r.iso === today,
          dateLabel: formatDateDdMmYyyy(r.iso),
        }));
      setDayRows(rows);
    } catch (err) {
      hrmsErrorToast(err, "Could not load attendance.");
      setDayRows([]);
    } finally {
      setLoading(false);
    }
  }, [user.branchId, user.uaId, viewYear, viewMonth, today]);

  useEffect(() => {
    void loadMonth();
  }, [loadMonth]);

  if (!user.branchId || !user.uaId) {
    return (
      <div className="text-muted small py-4 text-center">
        <Icon
          icon="solar:user-id-line-duotone"
          width={32}
          className="d-block mx-auto mb-2 opacity-50"
        />
        Your account is missing employee or branch details. Contact your
        administrator to view attendance.
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-center gap-3 mb-4">
        <button
          type="button"
          className="btn btn-link p-0 border-0 shadow-none text-decoration-none d-inline-flex align-items-center"
          style={{ color: "#6c757d" }}
          onClick={() => shiftMonth(-1)}
          disabled={loading}
          aria-label="Previous month"
        >
          <Icon
            icon="solar:alt-arrow-left-linear"
            width={22}
            style={{ color: "#6c757d" }}
          />
        </button>
        <span className="fw-semibold mb-0 text-dark">{monthLabel}</span>
        <button
          type="button"
          className="btn btn-link p-0 border-0 shadow-none text-decoration-none d-inline-flex align-items-center"
          style={{ color: "#6c757d" }}
          onClick={() => shiftMonth(1)}
          disabled={loading}
          aria-label="Next month"
        >
          <Icon
            icon="solar:alt-arrow-right-linear"
            width={22}
            style={{ color: "#6c757d" }}
          />
        </button>
      </div>

      <div
        className={`row g-0 mb-4 border rounded overflow-hidden ${cardClass}`}
      >
        <div className="col-6 col-md-4 border-end border-bottom border-md-bottom-0">
          <AttendanceStat
            label="Total Present"
            value={loading ? "—" : monthSummary.present}
          />
        </div>
        <div className="col-6 col-md-4 border-end border-bottom border-md-bottom-0">
          <AttendanceStat
            label="Total Absent"
            value={loading ? "—" : monthSummary.absent}
          />
        </div>
        <div className="col-12 col-md-4">
          <AttendanceStat
            label="Total working hours"
            value={loading ? "—" : monthSummary.totalHours}
          />
        </div>
      </div>

      <div className={`table-responsive mb-4 border ${cardClass}`}>
        <table className={`table text-nowrap mb-0 align-middle ${tableClass}`}>
          <thead className="text-dark fs-4">
            <tr>
              <th>Date</th>
              <th>Punch-in</th>
              <th>Punch-out</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : displayedRows.length > 0 ? (
              displayedRows.map((row) => (
                <tr key={row.iso}>
                  <td>
                    {row.dateLabel}
                    {row.isToday ? (
                      <span className="text-muted small ms-1">(Today)</span>
                    ) : null}
                  </td>
                  <td>
                    {row.punch_in ? formatDisplayTime(row.punch_in) : "—"}
                  </td>
                  <td>
                    {row.punch_out ? formatDisplayTime(row.punch_out) : "—"}
                  </td>
                  <td>{row.duration || "—"}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center">
                  No attendance records this month.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        totalItems={dayRows.length}
        itemsPerPage={itemsPerPage}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        itemsPerPageOptions={[10, 20, 30, 50, 100]}
      />
    </div>
  );
}
